import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { FileError } from '../../components/FileError';
import { FILE_LIMITS, validateFile } from '../../lib/fileValidation';
import {
  PLATFORMS,
  PLATFORM_LINKEDIN,
  PLATFORM_INSTAGRAM,
  PLATFORM_FACEBOOK,
  LANGUAGE_EN,
  LANGUAGE_PL,
} from '../../lib/constants';

const DEFAULT_PLATFORMS = [PLATFORM_LINKEDIN, PLATFORM_INSTAGRAM, PLATFORM_FACEBOOK];

const IMPORT_CARDS = [
  { key: 'screenshot', icon: '📸' },
  { key: 'email',      icon: '📧' },
  { key: 'pdf',        icon: '📄' },
  { key: 'pasteText',  icon: '📝' },
];

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let value = bytes;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function makeId() {
  return Math.random().toString(36).slice(2, 11);
}

// ─────────────────────────────────────────────────────────────────────────────
// Drop zone with drag-and-drop, click-to-browse, mock progress per file
// ─────────────────────────────────────────────────────────────────────────────
function DropZone({ files, onAdd, onRemove }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const inputRef = useRef(null);

  function handleDrop(event) {
    event.preventDefault();
    setActive(false);
    if (event.dataTransfer.files?.length) onAdd(event.dataTransfer.files);
  }

  function handleSelect(event) {
    if (event.target.files?.length) onAdd(event.target.files);
    event.target.value = '';
  }

  return (
    <div>
      <div
        className={'dropzone' + (active ? ' dropzone--active' : '')}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={handleDrop}
      >
        <div className="dropzone__icon" aria-hidden="true">📤</div>
        <div className="dropzone__title">{t('upload.dropZone')}</div>
        <div className="dropzone__subtitle">{t('upload.browse')}</div>
        <div className="dropzone__limits">
          {t('upload.acceptedImages', { limit: FILE_LIMITS.imageMb })}
        </div>
        <div className="dropzone__limits">
          {t('upload.acceptedVideos', { limit: FILE_LIMITS.videoMb })}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic,image/gif,video/mp4,video/quicktime,video/webm"
          onChange={handleSelect}
          style={{ display: 'none' }}
        />
      </div>

      {files.length > 0 && (
        <div className="file-strip" aria-label={t('upload.selectedFiles')}>
          {files.map((file) => (
            <div
              key={file.id}
              className={
                'file-card' +
                (file.valid ? ' file-card--accepted' : ' file-card--rejected')
              }
            >
              <button
                type="button"
                className="file-card__remove"
                onClick={() => onRemove(file.id)}
                aria-label={t('upload.removeFile')}
              >
                ×
              </button>
              <div className="file-card__thumb">
                {file.previewUrl ? (
                  <img src={file.previewUrl} alt="" />
                ) : (
                  <span aria-hidden="true">{file.kind === 'video' ? '🎬' : '📄'}</span>
                )}
              </div>
              <div className="file-card__name" title={file.name}>{file.name}</div>
              <div className="file-card__meta">{formatBytes(file.size)}</div>
              {file.valid ? (
                <div className="file-card__progress">
                  <div
                    className={
                      'file-card__progress-bar' +
                      (file.progress >= 100 ? ' file-card__progress-bar--done' : '')
                    }
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              ) : (
                <FileError errorKey={file.errorKey} params={file.errorParams} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Import (OCR) mode — placeholder cards
// ─────────────────────────────────────────────────────────────────────────────
function ImportMode() {
  const { t } = useTranslation();
  const [showSoon, setShowSoon] = useState(false);

  return (
    <div>
      <div className="import-intro">
        <h2 className="section-title">{t('upload.import.title')}</h2>
        <div className="page-header__subtitle">{t('upload.import.subtitle')}</div>
      </div>
      <div className="import-cards">
        {IMPORT_CARDS.map((card) => (
          <button
            key={card.key}
            type="button"
            className="import-card"
            onClick={() => setShowSoon(true)}
          >
            <div className="import-card__icon" aria-hidden="true">{card.icon}</div>
            <div className="import-card__title">
              {t(`upload.import.cards.${card.key}.title`)}
            </div>
            <div className="import-card__desc">
              {t(`upload.import.cards.${card.key}.description`)}
            </div>
          </button>
        ))}
      </div>
      {showSoon && (
        <div className="import-soon">🚧 {t('upload.import.comingSoon')}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export function UploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState('manual');
  const [files, setFiles] = useState([]);
  const [eventName, setEventName] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set(DEFAULT_PLATFORMS));
  const [language, setLanguage] = useState(LANGUAGE_PL);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // TODO(api): replace with real upload via POST /api/v1/events/:id/media (multipart).
  // The server re-runs validation (magic bytes + MIME + size) — never trust this layer for security.
  function addFiles(fileList) {
    const newFiles = Array.from(fileList).map((file) => {
      const result = validateFile(file, 'media');
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return {
        id: makeId(),
        name: file.name,
        size: file.size,
        kind: isVideo ? 'video' : 'image',
        previewUrl: isImage && result.valid ? URL.createObjectURL(file) : null,
        progress: 0,
        valid: result.valid,
        errorKey: result.valid ? null : result.errorKey,
        errorParams: result.valid ? null : result.params,
      };
    });
    setFiles((prev) => [...prev, ...newFiles]);
    newFiles.filter((f) => f.valid).forEach((f) => simulateUpload(f.id));
  }

  function simulateUpload(id) {
    const interval = setInterval(() => {
      setFiles((prev) => {
        let done = false;
        const next = prev.map((file) => {
          if (file.id !== id) return file;
          const updated = Math.min(100, file.progress + Math.random() * 18 + 6);
          if (updated >= 100) done = true;
          return { ...file, progress: updated };
        });
        if (done) clearInterval(interval);
        return next;
      });
    }, 220);
  }

  function removeFile(id) {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }

  // Revoke any remaining preview URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // True only when files exist AND every one of them failed validation.
  // Empty file list leaves the buttons enabled — the user can still create
  // an event without media.
  const allFilesRejected = files.length > 0 && files.every((f) => !f.valid);

  function togglePlatform(platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  }

  // TODO(media,ai): once media upload + caption generation land, this should
  // (1) create the event, (2) POST each file to /api/v1/events/:id/media,
  // (3) for the "generate" intent, kick off caption generation, then navigate.
  // For now both buttons just create the event and navigate to its detail screen.
  async function submitEvent(_intent /* 'upload' | 'generate' */) {
    if (submitting) return;
    setError(null);
    const trimmedName = eventName.trim();
    if (!trimmedName) {
      setError(t('upload.errorNameRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const event = await api.events.create({
        name: trimmedName,
        language,
        notes: notes.trim() || null,
        status: 'draft',
      });
      navigate(`/events/${event.id}`);
    } catch (err) {
      setError(err?.message || t('upload.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <header className="page-header">
        <h1 className="page-header__title">{t('upload.title')}</h1>
        <div className="page-header__subtitle">{t('upload.subtitle')}</div>
      </header>

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'manual'}
          className={'tab' + (mode === 'manual' ? ' tab--active' : '')}
          onClick={() => setMode('manual')}
        >
          {t('upload.modes.manual')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'import'}
          className={'tab' + (mode === 'import' ? ' tab--active' : '')}
          onClick={() => setMode('import')}
        >
          {t('upload.modes.import')}
        </button>
      </div>

      {mode === 'manual' ? (
        <div className="upload-grid">
          <DropZone files={files} onAdd={addFiles} onRemove={removeFile} />

          <form className="upload-form" onSubmit={(e) => e.preventDefault()}>
            <label className="form-field">
              <span className="form-field__label">{t('upload.eventName')}</span>
              <input
                type="text"
                className="form-field__input"
                value={eventName}
                placeholder={t('upload.eventNamePlaceholder')}
                onChange={(e) => setEventName(e.target.value)}
              />
            </label>

            <div className="form-field">
              <span className="form-field__label">{t('upload.platforms')}</span>
              <div className="checkbox-group">
                {PLATFORMS.filter((p) => p !== 'youtube').map((platform) => (
                  <label key={platform} className="checkbox-group__item">
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.has(platform)}
                      onChange={() => togglePlatform(platform)}
                    />
                    {t(`platforms.${platform}`)}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <span className="form-field__label">{t('upload.language')}</span>
              <div className="radio-group">
                {[LANGUAGE_PL, LANGUAGE_EN].map((lang) => (
                  <label key={lang}>
                    <input
                      type="radio"
                      name="upload-language"
                      checked={language === lang}
                      onChange={() => setLanguage(lang)}
                    />
                    {lang === LANGUAGE_PL ? '🇵🇱 Polski' : '🇬🇧 English'}
                  </label>
                ))}
              </div>
            </div>

            <label className="form-field">
              <span className="form-field__label">{t('upload.notes')}</span>
              <textarea
                className="form-field__textarea"
                value={notes}
                placeholder={t('upload.notesPlaceholder')}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>

            {error && <div className="modal__error" role="alert">{error}</div>}

            {allFilesRejected && (
              <div className="modal__error" role="alert">
                {t('upload.allRejected')}
              </div>
            )}

            <div className="upload-form__actions">
              <button
                type="button"
                className="button button--primary"
                disabled={submitting || allFilesRejected}
                onClick={() => submitEvent('generate')}
              >
                {submitting ? t('upload.submitting') : t('upload.generate')}
              </button>
              <button
                type="button"
                className="button button--ghost"
                disabled={submitting || allFilesRejected}
                onClick={() => submitEvent('upload')}
              >
                {t('upload.uploadOnly')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <ImportMode />
      )}
    </div>
  );
}
